import {ActionProvider} from '@github-ui/code-view-shared/contexts/ActionContext'
import {AllShortcutsEnabledProvider} from '@github-ui/code-view-shared/contexts/AllShortcutsEnabledContext'
import {CodeViewBannersProvider} from '@github-ui/code-view-shared/contexts/CodeViewBannersContext'
import {EditPermissionsProvider} from '@github-ui/code-view-shared/contexts/EditPermissionsContext'
import {FileQueryProvider} from '@github-ui/code-view-shared/contexts/FileQueryContext'
import {OpenPanelProvider} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {PathProvider} from '@github-ui/code-view-shared/contexts/PathContext'
import {RefInfoProvider} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {LayoutPayload, ReposAppPayload} from '@github-ui/code-view-types'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   useAppPayload is deprecated. See
   https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
import {AppPayloadContext} from '@github-ui/react-core/use-app-payload'
import type {UseSuspenseQueryResult} from '@github-ui/react-query'
import {Link, Outlet} from '@github-ui/react-router'
import {FileTreeControlProvider, useTreePane} from '@github-ui/repos-file-tree-view'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize} from '@github-ui/screen-size'
import {GlobalCommands} from '@github-ui/ui-commands'
import {useCallback, useRef} from 'react'

import {AppWrapper} from '../App'
import {symbolsHeaderId} from '../components/blob/BlobContent/CodeNav/CodeNavSymbolNavigation'
import {ScrollMarksContainer} from '../components/blob/BlobContent/CodeNav/ScrollMarksContainer'
import {PermalinkShortcut} from '../components/PermalinkShortcut'
import {CodeNavProvider} from '../contexts/CodeNavContext'
import {TreePaneProvider} from '../contexts/TreePaneContext'
import {useRouteAction} from '../hooks/use-route-action'
import {updateExpandPreferences} from '../hooks/use-update-panel-expand-preferences'
import {codeViewLayoutRoute} from '../routes/code-view-layout-route'
import {codeViewRepoRoute} from '../routes/code-view-repo-route'
import {textAreaId} from '../utilities/lines'

const REPOS_FILE_TREE_ID = 'repos-file-tree'

export function CodeViewLayout() {
  const {data} = useRouteQuery(codeViewLayoutRoute, 'mainQuery')
  const {data: appPayload} = useLayoutAppPayload()

  return (
    <AppPayloadContext value={appPayload}>
      <AppWrapper payload={data} repo={data.repo}>
        {/*
         This hidden link exposes the repository route as a stable DOM hook.
         It is used for programmatic navigation when clicking on the Code tab.
       */}
        <Link
          to={codeViewRepoRoute.generatePath({owner: data.repo.ownerLogin, repo: data.repo.name})}
          hidden
          id="code-view-repo-link"
        />

        <CodeViewLayoutInternal data={data} />
      </AppWrapper>
    </AppPayloadContext>
  )
}

function useLayoutAppPayload(): UseSuspenseQueryResult<ReposAppPayload> {
  return useRouteQuery(codeViewLayoutRoute, 'mainQuery', {
    select: ({payload: {helpUrl, findFileWorkerPath, findInFileWorkerPath, githubDevUrl}}) => ({
      helpUrl,
      findFileWorkerPath,
      findInFileWorkerPath,
      githubDevUrl,
    }),
  } as Parameters<typeof useRouteQuery>[2])
}

function CodeViewLayoutInternal({data}: {data: LayoutPayload}) {
  const openPanelRef = useRef<string | undefined>(undefined)
  const treeRef = useRef<HTMLDivElement>(null)
  // when user presses cmd+f6 this controls whether we focus the tree or the content
  const contentFocusedRef = useRef(false)
  const textAreaFocusedRef = useRef(false)
  // when we focus the content we can try to return focus to the element which previously had it
  const contentFocusTargetRef = useRef<HTMLElement | null>(null)
  // when we focus the tree we can try to return focus to the element which previously had it
  const treeFocusTargetRef = useRef<HTMLElement | null>(null)

  const updateTreeExpandedPreference = (isTreeExpanded: boolean) => {
    updateExpandPreferences(isTreeExpanded, null, null)
  }

  const treePaneState = useTreePane(
    REPOS_FILE_TREE_ID,
    openPanelRef,
    data.treeExpanded,
    textAreaId,
    updateTreeExpandedPreference,
  )

  const onFindFilesShortcut = useCallback(() => {
    if (window.matchMedia(`(max-width: ${ScreenSize.large - 1}px)`).matches) {
      treePaneState.expandTree({focus: 'search'})
    }
  }, [treePaneState])

  function toggleFocus() {
    const contentElement = document.querySelector<HTMLDivElement>('[data-selector="repos-split-pane-content"]')
    const symbolsPaneElement = document.getElementById(symbolsHeaderId)
    const textAreaElement = document.getElementById(textAreaId)
    if (document.activeElement?.id === textAreaId) {
      textAreaFocusedRef.current = true
    }
    // the user may have moved focus from where we last put it
    if (contentElement?.contains(document.activeElement) && !textAreaFocusedRef.current) {
      //the content (but not the text area) is focused
      contentFocusedRef.current = true
    } else if (treeRef.current?.contains(document.activeElement)) {
      contentFocusedRef.current = false
    }
    if (!contentFocusedRef.current && !textAreaFocusedRef.current) {
      // focus the text area
      const focusTarget = textAreaElement || contentElement
      treeFocusTargetRef.current = treeRef.current?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      focusTarget?.focus()
    } else if (textAreaFocusedRef.current) {
      //focus the content
      const focusTarget = contentFocusTargetRef.current || symbolsPaneElement || contentElement
      contentFocusedRef.current = true
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    } else {
      // focus the tree
      const focusTarget = treeFocusTargetRef.current || treeRef.current
      contentFocusTargetRef.current = contentElement?.contains(document.activeElement)
        ? (document.activeElement as HTMLElement)
        : null
      contentFocusedRef.current = false
      textAreaFocusedRef.current = false
      focusTarget?.focus()
    }
  }

  const action = useRouteAction()

  return (
    <AllShortcutsEnabledProvider allShortcutsEnabled={data.allShortcutsEnabled}>
      <FileTreeControlProvider>
        <FileQueryProvider>
          <CodeNavProvider>
            <TreePaneProvider treePaneState={treePaneState} onFindFilesShortcut={onFindFilesShortcut}>
              <PathProvider path={data.path}>
                <ActionProvider action={action}>
                  <RefInfoProvider refInfo={data.refInfo}>
                    <EditPermissionsProvider
                      canEdit={data.refInfo.canEdit}
                      canEditOnDefaultBranch={data.refInfo.canEditOnDefaultBranch}
                      fileExistsOnDefault={data.refInfo.fileExistsOnDefault}
                    >
                      <PermalinkShortcut />
                      <div>
                        <OpenPanelProvider payload={data} openPanelRef={openPanelRef} action={action}>
                          <CodeViewBannersProvider>
                            <Outlet />
                          </CodeViewBannersProvider>
                        </OpenPanelProvider>
                        <ScrollMarksContainer />
                        {/* TODO: make this focus on the cursor instead of just the blob as a whole */}
                        <GlobalCommands commands={{'code-view:toggle-focused-pane': toggleFocus}} />
                      </div>
                    </EditPermissionsProvider>
                  </RefInfoProvider>
                </ActionProvider>
              </PathProvider>
            </TreePaneProvider>
          </CodeNavProvider>
        </FileQueryProvider>
      </FileTreeControlProvider>
    </AllShortcutsEnabledProvider>
  )
}
